/**
 * 商朝游戏状态管理
 * 处理剧本初始化、势力选择、游戏开始
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Scenario } from '../data/shangDynastyData'
import {
  SHANG_FACTIONS,
  SHANG_CITIES,
  getFactionById,
  getScenarioById,
  getCitiesByFaction,
} from '../data/shangDynastyData'

// ==================== 类型定义 ====================

/** 游戏进行状态 */
export type GamePhase = 'setup' | 'playing' | 'paused' | 'ended'

/** 势力在游戏中的状态 */
export interface FactionState {
  factionId: string
  // 当前资源
  resources: {
    food: number
    money: number
    population: number
    prestige: number
  }
  // 拥有的城邑
  ownedCities: string[]
  // 外交关系
  relations: Record<string, 'ally' | 'neutral' | 'hostile' | 'war'>
  // 科技
  technologies: string[]
  // 君主名
  rulerName: string
}

/** 城邑在游戏中的状态 */
export interface CityState {
  cityId: string
  factionId: string
  // 当前人口
  population: number
  // 当前资源
  resources: {
    food: number
    money: number
    wood: number
    metal: number
  }
  // 建筑
  buildings: {
    id: string
    name: string
    level: number
  }[]
  // 驻军
  garrison: number
  // 民心
  morale: number
}

/** 游戏配置 */
export interface GameConfig {
  scenarioId: string
  playerFactionId: string
  playerRulerName: string
  gameSpeed: number
}

/** 商朝游戏状态 */
export interface ShangGameState {
  // 游戏配置
  config: GameConfig | null
  // 游戏阶段
  phase: GamePhase
  // 当前时间
  gameDate: { year: number; month: number }
  // 势力状态
  factionStates: Record<string, FactionState>
  // 城邑状态
  cityStates: Record<string, CityState>
  // 当前选中的势力/城邑
  selectedFactionId: string | null
  selectedCityId: string | null

  // 操作
  initializeGame: (scenarioId: string, playerFactionId: string, rulerName?: string) => void
  setPhase: (phase: GamePhase) => void
  setGameSpeed: (speed: number) => void
  tick: () => void
  selectFaction: (factionId: string | null) => void
  selectCity: (cityId: string | null) => void
  getPlayerFaction: () => FactionState | null
  getPlayerCities: () => CityState[]
  getCityState: (cityId: string) => CityState | null
  getFactionState: (factionId: string) => FactionState | null
  getPlayerCapital: () => CityState | null
  getPlayerCapitalName: () => string
  resetGame: () => void
}

// ==================== 辅助函数 ====================

/** 根据剧本初始化势力状态 */
const initializeFactionStates = (
  _scenario: Scenario,
  playerFactionId: string,
  playerRulerName?: string
): Record<string, FactionState> => {
  const states: Record<string, FactionState> = {}

  // 为每个势力创建初始状态
  SHANG_FACTIONS.forEach((faction) => {
    const factionCities = getCitiesByFaction(faction.id)
    const isPlayer = faction.id === playerFactionId

    // 根据势力类型和剧本设置初始资源
    let initialResources = {
      food: 1000,
      money: 500,
      population: 5000,
      prestige: 50,
    }

    // 商王室资源更多
    if (faction.type === 'royal') {
      initialResources = {
        food: 5000,
        money: 3000,
        population: 20000,
        prestige: 100,
      }
    }

    // 玩家额外奖励
    if (isPlayer) {
      initialResources.food += 1000
      initialResources.money += 500
      initialResources.prestige += 20
    }

    // 设置外交关系
    const relations: Record<string, 'ally' | 'neutral' | 'hostile' | 'war'> = {}
    SHANG_FACTIONS.forEach((other) => {
      if (other.id === faction.id) return

      // 根据初始关系设置
      if (faction.initialRelation === 'hostile' && other.initialRelation === 'hostile') {
        relations[other.id] = 'hostile'
      } else if (faction.initialRelation === 'vassal' && other.id === 'shang') {
        relations[other.id] = 'ally'
      } else if (other.initialRelation === 'vassal' && faction.id === 'shang') {
        relations[other.id] = 'ally'
      } else {
        relations[other.id] = 'neutral'
      }
    })

    states[faction.id] = {
      factionId: faction.id,
      resources: initialResources,
      ownedCities: factionCities.map((c) => c.id),
      relations,
      technologies: [],
      rulerName: isPlayer ? (playerRulerName || '玩家') : faction.name + '首领',
    }
  })

  return states
}

/** 根据剧本初始化城邑状态 */
const initializeCityStates = (): Record<string, CityState> => {
  const states: Record<string, CityState> = {}

  SHANG_CITIES.forEach((city) => {
    // 根据城邑等级设置初始值
    const levelMultiplier =
      city.level === 'capital' ? 3 : city.level === 'large' ? 2 : city.level === 'medium' ? 1.5 : 1

    states[city.id] = {
      cityId: city.id,
      factionId: city.factionId,
      population: Math.floor(city.population * (0.8 + Math.random() * 0.4)), // 随机波动
      resources: {
        food: Math.floor(city.resources.food * levelMultiplier),
        money: Math.floor(city.resources.money * levelMultiplier),
        wood: Math.floor(city.resources.wood * levelMultiplier),
        metal: Math.floor(city.resources.metal * levelMultiplier),
      },
      buildings: city.buildings.map((b) => ({
        id: b,
        name: b,
        level: 1,
      })),
      garrison: Math.floor(city.population * 0.1), // 10%人口作为驻军
      morale: 70 + Math.floor(Math.random() * 20), // 70-90民心
    }
  })

  return states
}

// ==================== Store 实现 ====================

export const useShangGameStore = create<ShangGameState>()(
  persist(
    (set, get) => ({
      // 初始状态
      config: null,
      phase: 'setup',
      gameDate: { year: -1250, month: 1 }, // 默认武丁时期
      factionStates: {},
      cityStates: {},
      selectedFactionId: null,
      selectedCityId: null,

      // 初始化游戏
      initializeGame: (scenarioId, playerFactionId, rulerName) => {
        const scenario = getScenarioById(scenarioId)
        if (!scenario) {
          console.error('剧本不存在:', scenarioId)
          return
        }

        const playerFaction = getFactionById(playerFactionId)
        if (!playerFaction) {
          console.error('势力不存在:', playerFactionId)
          return
        }

        // 初始化势力状态
        const factionStates = initializeFactionStates(scenario, playerFactionId, rulerName)

        // 初始化城邑状态
        const cityStates = initializeCityStates()

        set({
          config: {
            scenarioId,
            playerFactionId,
            playerRulerName: rulerName || '玩家',
            gameSpeed: 1,
          },
          phase: 'playing',
          gameDate: { year: scenario.startYear, month: 1 },
          factionStates,
          cityStates,
          selectedFactionId: playerFactionId,
          selectedCityId: null,
        })

        console.log('游戏初始化完成:', {
          scenario: scenario.name,
          player: playerFaction.name,
          ruler: rulerName || '玩家',
          year: scenario.startYear,
        })
      },

      // 设置游戏阶段
      setPhase: (phase) => set({ phase }),

      // 设置游戏速度
      setGameSpeed: (speed) =>
        set((state) => ({
          config: state.config ? { ...state.config, gameSpeed: speed } : null,
        })),

      // 时间推进（每月）
      tick: () => {
        const state = get()
        if (state.phase !== 'playing') return

        set((state) => {
          let { year, month } = state.gameDate
          month++
          if (month > 12) {
            month = 1
            year++
          }

          // 更新资源（简化版）
          const newFactionStates = { ...state.factionStates }
          Object.keys(newFactionStates).forEach((factionId) => {
            const factionState = newFactionStates[factionId]
            // 基础收入
            const income = {
              food: factionState.ownedCities.length * 100,
              money: factionState.ownedCities.length * 50,
              population: Math.floor(factionState.resources.population * 0.01), // 1%增长
              prestige: 0,
            }

            newFactionStates[factionId] = {
              ...factionState,
              resources: {
                food: factionState.resources.food + income.food,
                money: factionState.resources.money + income.money,
                population: factionState.resources.population + income.population,
                prestige: factionState.resources.prestige + income.prestige,
              },
            }
          })

          return {
            gameDate: { year, month },
            factionStates: newFactionStates,
          }
        })
      },

      // 选择势力
      selectFaction: (factionId) => set({ selectedFactionId: factionId }),

      // 选择城邑
      selectCity: (cityId) => set({ selectedCityId: cityId }),

      // 获取玩家势力
      getPlayerFaction: () => {
        const state = get()
        if (!state.config) return null
        return state.factionStates[state.config.playerFactionId] || null
      },

      // 获取玩家城邑
      getPlayerCities: () => {
        const state = get()
        const playerFaction = state.getPlayerFaction()
        if (!playerFaction) return []
        return playerFaction.ownedCities
          .map((cityId) => state.cityStates[cityId])
          .filter(Boolean)
      },

      // 获取城邑状态
      getCityState: (cityId) => {
        const state = get()
        return state.cityStates[cityId] || null
      },

      // 获取势力状态
      getFactionState: (factionId) => {
        const state = get()
        return state.factionStates[factionId] || null
      },

      // 获取玩家的国都
      getPlayerCapital: () => {
        const state = get()
        const playerFaction = state.getPlayerFaction()
        if (!playerFaction) return null

        // 找到玩家拥有的第一个都城级别的城邑，或第一个城邑作为国都
        const playerCities = playerFaction.ownedCities
          .map((cityId) => state.cityStates[cityId])
          .filter(Boolean)

        // 优先返回标记为isStartingCapital的城邑
        const capital = playerCities.find((city) => {
          const cityData = SHANG_CITIES.find((c) => c.id === city?.cityId)
          return cityData?.isStartingCapital
        })

        // 如果没有标记的都城，返回第一个城邑
        return capital || playerCities[0] || null
      },

      // 获取玩家国都的名称
      getPlayerCapitalName: () => {
        const state = get()
        const capital = state.getPlayerCapital()
        if (!capital) return '未知城邑'

        const cityData = SHANG_CITIES.find((c) => c.id === capital.cityId)
        return cityData?.name || '未知城邑'
      },

      // 重置游戏
      resetGame: () =>
        set({
          config: null,
          phase: 'setup',
          gameDate: { year: -1250, month: 1 },
          factionStates: {},
          cityStates: {},
          selectedFactionId: null,
          selectedCityId: null,
        }),
    }),
    {
      name: 'huaxia-chronicles-shang-game',
      partialize: (state) => ({
        config: state.config,
        phase: state.phase,
        gameDate: state.gameDate,
        factionStates: state.factionStates,
        cityStates: state.cityStates,
      }),
    }
  )
)
